import { Fragment, useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import { useRouter } from 'next/router';
import dynamic from 'next/dynamic';
import ProductCard from '@components/ProductCardNew/ProductCard';
import { IProductObject, TAlgoliaProductHit } from '@features/categories/typescript';
import styles from '@components/ProductList/ProductList.styles';
import { IInfoCardWithMedia, IParsedImage } from '@components/typescript';
import InfoCardWithMedia from '@components/InfoCardWithMedia/InfoCardWithMedia';
import { PRODUCTS_RENDER_COUNTS, SPECIAL_ITEM_POSITIONS } from '@features/categories/const';
import ArrangementProductState from '@features/categories/mobx/state/ArrangementProductState';
import ShopTheLook from '@components/ShopTheLook/ShopTheLook';
import ReaffiliatesProductListing from '@reaffiliates/ReaffiliatesProductListing';
import { useOffer } from '@hooks/useOffer';
import ReaffiliatesSelectItem from '@reaffiliates/ReaffiliatesSelectItem';
import LayoutUiState from '@features/layout/mobX/LayoutUiState';
import { logScrollTrigger } from '@utils/scrollDebug';
import { LISTING_PRODUCT_DATA_ATTR, scrollToListingProduct } from '@utils/scrollToListingProduct';
import { TProductGallerySlide } from '@features/cms/typescript';
import ProductGalleryBlockModal from '@features/cms/components/collections/ProductGalleryBlock/ProductGalleryBlockModal/ProductGalleryBlockModal';
import useBodyOverflow from '@hooks/useBodyOverflow';
import { IHitsInsights } from '@components/Search/typescript';
const DynamicModalRootPortal = dynamic(() => import('@components/ModalRootPortal/ModalRootPortal'), {
  ssr: false
});
type PropsType = {
  products: IProductObject[];
  infoCards?: IInfoCardWithMedia[];
  arrangementProducts?: ArrangementProductState[];
  getArrangementProductProducts?: (arrangementProductId: number) => Promise<IProductObject[]>;
  category?: string;
  productsCount?: number;
  listingType?: string;
  listingId?: number;
  hitsInsights?: IHitsInsights<TAlgoliaProductHit>;
  onProductClick?: () => void;
};
const ProductList = ({
  products,
  infoCards = undefined,
  arrangementProducts = undefined,
  getArrangementProductProducts = undefined,
  category = '',
  productsCount = 0,
  listingType = 'product_listing',
  listingId = 0,
  hitsInsights = {
    hits: [],
    sendEvent: () => null
  },
  onProductClick = undefined
}: PropsType) => {
  const [isModalOpen, setModalOpen] = useState(false);
  const [modalData, setModalData] = useState<TProductGallerySlide<IParsedImage> | null>(null);
  const lastVisitedProductRef = useRef<HTMLDivElement>(null);
  const {
    setLastVisitedProductId,
    lastVisitedProductId
  } = LayoutUiState;
  const router = useRouter();
  const {
    query
  } = router;
  const {
    slug
  } = query;
  const [_, priceListName] = useOffer();
  const newSlug = slug && slug?.length && slug[1] ? slug[1] : '';
  const productsLength = products.length;
  useBodyOverflow(isModalOpen, 'productList', false, true);

  // Browser back only. Retry after routeChangeComplete / CollectionLayout — they scroll to top otherwise.
  useEffect(() => {
    if (!lastVisitedProductId || !LayoutUiState.listingScrollRestorePending) return undefined;
    const isTargetInList = products.some(product => product.id === lastVisitedProductId);
    if (!isTargetInList) {
      logScrollTrigger('ProductList:restoreLastVisitedProduct — product not in list yet', {
        lastVisitedProductId,
        productsLength
      });
      return undefined;
    }
    logScrollTrigger('ProductList:restoreLastVisitedProduct — scheduled', {
      lastVisitedProductId,
      productsLength
    });
    let attempts = 0;
    let finishTimeoutId: number | undefined;
    const pendingTimeoutIds: number[] = [];
    const finishRestore = () => {
      setLastVisitedProductId(null);
      LayoutUiState.setListingScrollRestorePending(false);
    };
    const scheduleFinish = () => {
      if (finishTimeoutId !== undefined) window.clearTimeout(finishTimeoutId);
      finishTimeoutId = window.setTimeout(finishRestore, 1200);
    };
    const tryScroll = (): boolean => {
      const productId = LayoutUiState.lastVisitedProductId;
      if (!productId || !LayoutUiState.listingScrollRestorePending) return false;
      if (!products.some(product => product.id === productId)) return false;
      if (!scrollToListingProduct(productId, 'ProductList:restoreLastVisitedProduct')) return false;
      scheduleFinish();
      return true;
    };
    const retry = () => {
      if (tryScroll()) return;
      attempts += 1;
      if (attempts < 25) {
        const id = window.setTimeout(retry, 100);
        pendingTimeoutIds.push(id);
      }
    };
    const scheduleRetries = (delays: number[]) => {
      delays.forEach(delay => {
        const id = window.setTimeout(retry, delay);
        pendingTimeoutIds.push(id);
      });
    };
    scheduleRetries([50, 250, 400, 600, 900]);
    const onRouteChangeComplete = () => {
      scheduleRetries([0, 50, 350, 500, 800]);
    };
    router.events.on('routeChangeComplete', onRouteChangeComplete);
    return () => {
      pendingTimeoutIds.forEach(id => window.clearTimeout(id));
      if (finishTimeoutId !== undefined) window.clearTimeout(finishTimeoutId);
      router.events.off('routeChangeComplete', onRouteChangeComplete);
    };
  }, [lastVisitedProductId, productsLength, products, setLastVisitedProductId, router.events]);
  useEffect(() => {
    if (products.length) {
      ReaffiliatesProductListing(products, category || newSlug, priceListName, productsCount, listingType, listingId);
    }
  }, [products, category, newSlug, priceListName, productsCount, listingType, listingId]);
  const selectFunction = useCallback((product: IProductObject) => {
    if (onProductClick) {
      onProductClick();
    }
    ReaffiliatesSelectItem(product, category || newSlug, priceListName, listingType, listingId);
  }, [onProductClick, category, newSlug, priceListName, listingType, listingId]);
  const getModalData = useCallback(async (arrangementProduct: ArrangementProductState) => {
    if (getArrangementProductProducts) {
      setModalOpen(true);
      const productsData: IProductObject[] = await getArrangementProductProducts(arrangementProduct.id);
      setModalData({
        products: productsData,
        image: arrangementProduct.imageWithBlur,
        video: arrangementProduct.video,
        itemId: 'singleImage'
      });
    }
  }, [getArrangementProductProducts]);
  const handleCloseModal = useCallback(() => {
    setModalOpen(false);
    setModalData(null);
  }, []);
  const handleProductClick = useCallback((productClicked: IProductObject) => {
    // Persist before navigation so browser-back / remount can restore scroll position
    if (productClicked?.id) {
      logScrollTrigger('ProductList:handleProductClick — setLastVisitedProductId', {
        productId: productClicked.id
      });
      setLastVisitedProductId(productClicked.id);
    }
    selectFunction(productClicked);
    if (hitsInsights.hits.length) {
      const hit = hitsInsights.hits.find(hitObject => hitObject.id_product === productClicked.id);
      if (hit) {
        hitsInsights.sendEvent('click', hit, 'Hit Clicked');
      }
    }
  }, [selectFunction, hitsInsights, setLastVisitedProductId]);
  const renderProductList = useMemo(() => {
    let itemListIndex = -1;
    let infoCardIndex = -1;
    let arrangementIndex = -1;
    return products.map((product, productIndex) => {
      itemListIndex = itemListIndex >= PRODUCTS_RENDER_COUNTS ? 1 : itemListIndex + 1;
      const shouldShowSpecialCard = SPECIAL_ITEM_POSITIONS.includes(itemListIndex);
      const addRef = lastVisitedProductId === product.id;
      const renderSpecialCard = () => {
        if (itemListIndex === SPECIAL_ITEM_POSITIONS[0] && arrangementProducts && arrangementProducts.length) {
          arrangementIndex = arrangementIndex === arrangementProducts.length - 1 ? 0 : arrangementIndex + 1;
          return arrangementProducts[arrangementIndex].image.desktop || arrangementProducts[arrangementIndex].video.desktop.data ? <div sx={styles.specialCol}>
              <ShopTheLook arrangementProduct={arrangementProducts[arrangementIndex]} getModalData={getModalData} />
            </div> : null;
        }
        if (infoCards && infoCards.length) {
          infoCardIndex = infoCardIndex === infoCards.length - 1 ? 0 : infoCardIndex + 1;
          return <div sx={styles.specialCol}>
              <InfoCardWithMedia infoCard={infoCards[infoCardIndex]} />
            </div>;
        }
        return <></>;
      };

      // Używamy stabilnego key zamiast Math.random() - to zapobiega niepotrzebnym re-renderom
      return <Fragment key={`product-${product.id}-${productIndex}-${itemListIndex}`}>
          {shouldShowSpecialCard && renderSpecialCard()}
          <div sx={styles.productCol} ref={addRef ? lastVisitedProductRef : null} {...addRef ? {
          [LISTING_PRODUCT_DATA_ATTR]: product.id
        } : {}}>
            <ProductCard product={product} selectFunction={() => handleProductClick(product)} />
          </div>
        </Fragment>;
    });
  }, [products, arrangementProducts, infoCards, lastVisitedProductId, getModalData, handleProductClick]);
  return <>
      {renderProductList}
      {isModalOpen && modalData && <DynamicModalRootPortal>
          <ProductGalleryBlockModal isActive={isModalOpen} slide={modalData} onClose={handleCloseModal} />
        </DynamicModalRootPortal>}
    </>;
};
export default observer(ProductList);